import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * Unified pagination footer for admin tables.
 * Props:
 *  - total, page, pageSize, totalPages
 *  - onPageChange(n), onPageSizeChange(n)
 *  - pageSizeOptions (default [10, 30, 50])
 *  - testIdPrefix (default "admin")
 */
export const AdminPagination = ({
  total = 0,
  page = 1,
  pageSize = 10,
  totalPages = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  testIdPrefix = 'admin',
}) => {
  if (totalPages <= 0) return null;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div
      className="flex items-center justify-between mt-4 gap-4 flex-wrap"
      data-testid={`${testIdPrefix}-pagination`}
    >
      <div className="flex items-center gap-3">
        <span className="text-xs text-text_muted" data-testid={`${testIdPrefix}-pagination-info`}>
          {total === 0 ? 'No results' : `Showing ${from}–${to} of ${total}`}
        </span>
        <label className="flex items-center gap-1.5 text-xs text-text_muted">
          <span>Rows</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange?.(Number(v))}>
            <SelectTrigger
              className="w-20 h-8 text-xs"
              data-testid={`${testIdPrefix}-page-size-select`}
              aria-label="Rows per page"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`${testIdPrefix}-page-size-${n}`}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!canPrev}
          onClick={() => onPageChange?.(Math.max(1, page - 1))}
          className="h-8 px-3"
          data-testid={`${testIdPrefix}-pagination-prev`}
        >
          Previous
        </Button>
        <span className="text-xs text-text_muted px-2" data-testid={`${testIdPrefix}-pagination-page`}>
          Page {page} / {totalPages}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={!canNext}
          onClick={() => onPageChange?.(page + 1)}
          className="h-8 px-3"
          data-testid={`${testIdPrefix}-pagination-next`}
        >
          Next
        </Button>
      </div>
    </div>
  );
};
